/* xfetch method */
import { REGION } from '@ms/config/app.js'
import { API_PREFIX } from 'composables/useRegion.js'
import { getHeaders } from 'crypto-fetch/headers.js'
import { handleHttpResponse, parseResponseData } from './parser.js'

const VALID_METHODS = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'HEAD', 'OPTIONS']

/**
 * Makes an HTTP request using the Fetch API with the provided URL and options.
 *
 * @param {string} [url=''] - The URL to which the request is sent.
 * @param {RequestInit} [options={}] - The options for the request.
 * @param {string} [options.baseURL=API_PREFIX] - The base URL to be used if the URL does not start with '/api'.
 * @param {Object} [options.headers={}] - Additional headers to be sent with the request.
 * @param {Object|string} [options.body] - The body of the request. If it's an object, it will be stringified.
 * @param {boolean} [options.self=false] - A flag to determine if the response data should be parsed in a specific way.
 * @param {boolean} [options.encrypted=false] - A flag to determine if the response should be handled as encrypted.
 * @param {Object} [options.restConfig] - Additional configuration options for the request.
 * @returns {Promise<Object>} - A promise that resolves to the response data or an error object.
 *                eg. { error, data }
 */
export default async function xfetch(url = '', options = {}) {
  if (typeof url !== 'string') {
    throw new TypeError('URL must be a string')
  }
  if (options !== null && typeof options !== 'object') {
    throw new TypeError('Options must be an object')
  }

  const { baseURL = API_PREFIX, headers = {}, body, self = false, encrypted = false, ...restConfig } = options
  const platformHeaders = getHeaders()

  const config = {
    method: 'GET',
    headers: {
      ...platformHeaders,
      ...headers
    }
  }

  if (restConfig.method && !VALID_METHODS.includes(restConfig.method.toUpperCase())) {
    throw new Error(`Invalid HTTP method: ${restConfig.method}`)
  }

  if (body) {
    if (typeof body === 'object') {
      config.body = JSON.stringify(body)
      Object.assign(config.headers, { 'Content-Type': 'application/json' })
    } else {
      config.body = body
    }
  }

  if (restConfig) {
    Object.assign(config, restConfig)
  }

  let urlPath = url.startsWith('/api') ? url : `${baseURL}${url}`

  if (process.env.PLATFORM === 'local') {
    urlPath = process.env.VUE_APP_ORIGIN.replace(/xx/g, REGION) + urlPath
  }

  try {
    const res = await fetch(urlPath, config)
    const handledResponse = await handleHttpResponse(res, encrypted)
    const data = parseResponseData(handledResponse, self)
    return data
  } catch (error) {
    return { error }
  }
}
